import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Banknote, Plus } from "lucide-react";
import {
  ujmsCustodyHistory, ujmsCaseBonds, ujmsSetBond, ujmsBondAction, ujmsPlaceHold, ujmsLiftHold,
  ujmsCaseCustody, ujmsAuthorizeReleaseReview,
} from "../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, can, fmtDT } from "./ui";

const BOND_TYPES = ["D-BOND", "I-BOND", "C-BOND", "CASH"];
const HOLD_TYPES = ["WARRANT", "COURT_ORDER", "OTHER_JURISDICTION", "IMMIGRATION_DETAINER", "MEDICAL", "OTHER"];

export function CustodyBondTab({ caseId, access, onChanged }) {
  const [history, setHistory] = useState(null);
  const [bonds, setBonds] = useState([]);
  const [form, setForm] = useState({ bond_type: "D-BOND", amount: "", notes: "" });
  const [holdForm, setHoldForm] = useState({ hold_type: "WARRANT", reason: "" });
  const [busy, setBusy] = useState(false);
  const [releaseReviews, setReleaseReviews] = useState([]);

  const load = useCallback(() => {
    ujmsCustodyHistory(caseId).then((r) => setHistory(r.data.records)).catch(() => setHistory([]));
    ujmsCaseBonds(caseId).then((r) => setBonds(r.data)).catch(() => setBonds([]));
    ujmsCaseCustody(caseId).then((r) => setReleaseReviews(r.data.release_reviews || [])).catch(() => setReleaseReviews([]));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const setBond = async () => {
    setBusy(true);
    try {
      await ujmsSetBond(caseId, { ...form, amount: Number(form.amount) });
      toast.success(`BOND_SET — ${form.bond_type} $${Number(form.amount).toLocaleString()}`);
      setForm({ bond_type: "D-BOND", amount: "", notes: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to set bond"); }
    setBusy(false);
  };

  const act = async (b, action) => {
    const notes = ["FORFEIT", "REFUND", "EXONERATE"].includes(action)
      ? window.prompt(`${action} — notes (docketed):`, action === "FORFEIT" ? "Defendant failed to appear" : "") : "";
    if (notes === null) return;
    try {
      const r = await ujmsBondAction(b.id, action, notes || "");
      toast.success(`Bond ${r.data.status.toLowerCase()} — docketed`);
      if (r.data.custody_released) toast.success("Defendant released from custody — bond posted");
      if (r.data.custody_release_blocked) toast.error(`Custody release blocked: ${r.data.custody_release_blocked}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const placeHold = async (rec) => {
    try {
      await ujmsPlaceHold(rec.id, holdForm);
      toast.success(`HOLD_PLACED — ${holdForm.hold_type} (blocks release until lifted)`);
      setHoldForm({ hold_type: "WARRANT", reason: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to place hold"); }
  };

  const liftHold = async (h) => {
    const reason = window.prompt("Lift hold — reason (docketed):", "Hold resolved");
    if (reason === null) return;
    try {
      await ujmsLiftHold(h.id, reason || "Hold resolved");
      toast.success("HOLD_LIFTED");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to lift hold"); }
  };
  const authorizeRelease = async (review) => {
    const reason = window.prompt("Court authorization note (docketed):", "Release authority reviewed");
    if (reason === null) return;
    try { await ujmsAuthorizeReleaseReview(review.id, reason || "Release authority reviewed"); toast.success("Custody release authorized — Sheriff execution task created"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Authorization failed"); }
  };

  if (history === null) return <div className="text-sm text-slate-400 p-4">Loading custody & bond record…</div>;
  const activeRec = history.find((r) => r.status === "IN_CUSTODY");
  return (
    <div className="space-y-4" data-testid="tab-custody">
      <Panel title="pretrial detention & release timeline" testId="custody-history-panel"
        action={<span className="text-[11px] text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" /> {history.filter((r) => r.status === "IN_CUSTODY").length ? "DEFENDANT IN CUSTODY" : "not in custody"}</span>}>
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Booking #</th><th className={thCls}>Facility</th><th className={thCls}>Admitted</th>
            <th className={thCls}>Released</th><th className={thCls}>Release Reason</th><th className={thCls}>Holds</th><th className={thCls}>Status</th>
          </tr></thead>
          <tbody>
            {history.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`custody-rec-${r.id}`}>
                <td className={`${tdCls} font-mono text-xs`}>{r.booking_number || "—"}</td>
                <td className={tdCls}>{r.facility || "—"}</td>
                <td className={`${tdCls} font-mono text-xs`}>{fmtDT(r.admitted_at)}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.released_at ? fmtDT(r.released_at) : "—"}</td>
                <td className={`${tdCls} text-xs`}>{r.release_reason || (r.released_by ? `by ${r.released_by}` : "—")}</td>
                <td className={tdCls}>
                  {(r.holds || []).length
                    ? r.holds.map((h) => (
                      <span key={h.id} className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase border rounded-sm px-1.5 py-0.5 mr-1 mb-0.5 ${h.status === "ACTIVE" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-400"}`}
                        data-testid={`hold-chip-${h.id}`}>
                        {h.hold_type}{h.status === "ACTIVE" ? "" : " (lifted)"}
                        {h.status === "ACTIVE" && can(access, "custody.manage") && (
                          <button className="text-red-600 font-bold hover:text-red-800" title={`Lift ${h.hold_type} hold`}
                            onClick={() => liftHold(h)} data-testid={`hold-lift-${h.id}`}>×</button>
                        )}
                      </span>
                    )) : "—"}
                </td>
                <td className={tdCls}><Badge value={r.status} /></td>
              </tr>
            ))}
            {!history.length && <tr><td colSpan={7} className={`${tdCls} text-slate-400`}>No custody history on this case — the defendant has never been booked.</td></tr>}
          </tbody>
        </table>
        {activeRec && can(access, "custody.manage") && (
          <div className="flex flex-wrap items-end gap-2 px-3 py-2.5 border-t border-[#e3e9ef] bg-amber-50/40" data-testid="hold-place-form">
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Place hold on booking {activeRec.booking_number}</div>
              <select className={`${selectCls} w-48`} value={holdForm.hold_type}
                onChange={(e) => setHoldForm({ ...holdForm, hold_type: e.target.value })} data-testid="hold-type-select">
                {HOLD_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Reason</div>
              <input className={inputCls} value={holdForm.reason} onChange={(e) => setHoldForm({ ...holdForm, reason: e.target.value })}
                placeholder="e.g. Out-of-county warrant — hold for pickup" data-testid="hold-reason-input" />
            </div>
            <button className={btnPrimary} disabled={!holdForm.reason.trim()} onClick={() => placeHold(activeRec)} data-testid="hold-place-btn">
              <Lock className="w-4 h-4" /> Place Hold
            </button>
            <span className="text-[11px] text-slate-400 basis-full">Active holds block every release path — bond posting and "released pending trial" adjudications included.</span>
          </div>
        )}
      </Panel>

      {!!releaseReviews.length && (
        <Panel title="custody release reviews" testId="custody-release-reviews-panel">
          <table className="w-full border-collapse" data-testid="custody-release-reviews-table"><thead><tr><th className={thCls}>Authority</th><th className={thCls}>Reference</th><th className={thCls}>Requested</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
            <tbody>{releaseReviews.map((review) => <tr key={review.id} data-testid={`custody-release-review-${review.id}`}><td className={tdCls}>{review.authority_type?.replace(/_/g, " ")}</td><td className={`${tdCls} font-mono text-xs`}>{review.authority_reference}</td><td className={tdCls}>{review.requested_by}</td><td className={tdCls}><Badge value={review.status} /></td><td className={tdCls}>{review.status === "PENDING" && can(access, "filing.accept", "order.sign") && <button className={btnPrimary} onClick={() => authorizeRelease(review)} data-testid={`custody-authorize-release-${review.id}`}>Authorize</button>}</td></tr>)}</tbody>
          </table>
        </Panel>
      )}

      <Panel title="bonds — pretrial release" testId="bonds-panel">
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Type</th><th className={thCls}>Amount</th><th className={thCls}>Status</th>
            <th className={thCls}>Set By</th><th className={thCls}>Posted</th><th className={thCls}>Notes</th><th className={thCls}></th>
          </tr></thead>
          <tbody>
            {bonds.map((b) => (
              <tr key={b.id} className="hover:bg-slate-50" data-testid={`bond-row-${b.id}`}>
                <td className={`${tdCls} font-semibold`}>{b.bond_type}</td>
                <td className={`${tdCls} font-mono`}>${Number(b.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className={tdCls}><Badge value={b.status} /></td>
                <td className={`${tdCls} text-xs`}>{b.set_by}<div className="font-mono text-slate-400">{fmtDT(b.created_at)}</div></td>
                <td className={`${tdCls} text-xs`}>{b.posted_by ? <>{b.posted_by}<div className="font-mono text-slate-400">{fmtDT(b.posted_at)}</div></> : "—"}</td>
                <td className={`${tdCls} text-xs`}>{b.resolution_notes || b.notes || "—"}</td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  {can(access, "filing.accept", "hearing.manage", "finance.manage") && (
                    <>
                      {b.status === "SET" && <button className={btnGhost} onClick={() => act(b, "POST")} data-testid={`bond-post-${b.id}`}>Post Bond</button>}
                      {["SET", "POSTED"].includes(b.status) && (
                        <button className="inline-flex items-center gap-1 border border-red-300 bg-white text-red-700 text-sm font-medium px-2.5 py-1.5 rounded-sm hover:bg-red-50 ml-1"
                          onClick={() => act(b, "FORFEIT")} data-testid={`bond-forfeit-${b.id}`}>Forfeit</button>
                      )}
                      {b.status === "POSTED" && (
                        <>
                          <button className={`${btnGhost} ml-1`} onClick={() => act(b, "REFUND")} data-testid={`bond-refund-${b.id}`}>Refund</button>
                          <button className={`${btnGhost} ml-1`} onClick={() => act(b, "EXONERATE")} data-testid={`bond-exonerate-${b.id}`}>Exonerate</button>
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!bonds.length && <tr><td colSpan={7} className={`${tdCls} text-slate-400`}>No bonds on this case.</td></tr>}
          </tbody>
        </table>
        {can(access, "hearing.manage", "order.sign") && (
          <div className="flex flex-wrap items-end gap-2 px-3 py-2.5 border-t border-[#e3e9ef]" data-testid="bond-set-form">
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Bond type</div>
              <select className={`${selectCls} w-32`} value={form.bond_type} onChange={(e) => setForm({ ...form, bond_type: e.target.value })} data-testid="bond-type-select">
                {BOND_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Amount $</div>
              <input className={`${inputCls} w-32`} type="number" min="1" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bond-amount-input" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Notes</div>
              <input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Set at first appearance" data-testid="bond-notes-input" />
            </div>
            <button className={btnPrimary} disabled={busy || !form.amount || Number(form.amount) <= 0} onClick={setBond} data-testid="bond-set-btn">
              <Banknote className="w-4 h-4" /> Set Bond
            </button>
          </div>
        )}
      </Panel>
    </div>
  );
}
